import React, { useEffect, useState } from 'react';
import { Button, Card, Modal, Space, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import './GirisCikisTakvimi.css';

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;

// Giriş-Çıkış takvimi (03.11.2026): okutma + resmi tatil + izin (raporlu ayrı renk), gün gün. Profil ve İK › Personel ortak kullanır.
// Okutmalar (profil-giris-cikis: gün başına ilk/son okutma) ve resmi tatiller (profil-resmi-tatil) burada çekilir;
// izinler sayfanın zaten çektiği listedir (startDate, endDate, startTime, endTime, type, hours, reason, approval).

const AY_ADLARI = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const HAFTA_GUNLERI = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const GUN_ADLARI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const RENK_SINIF = { green: 'yesil', red: 'kirmizi', blue: 'mavi', orange: 'turuncu' };
const GIRIS_ACIKLAMA = { red: 'Geç giriş · 09:15 sonrası', blue: 'Erken giriş · 08:30 öncesi', green: 'Zamanında' };
const CIKIS_ACIKLAMA = { red: 'Erken çıkış · 18:30 öncesi', blue: '18:30 sonrası', green: 'Tam 18:30' };
const KISIM_ADI = { tam: 'Tam gün', sabah: 'Yarım gün · öğleden önce', ogle: 'Yarım gün · öğleden sonra' };
const ZEMIN = { tatil: 'var(--gc-tatil-zemin)', izin: 'var(--gc-izin-zemin)', rapor: 'var(--gc-rapor-zemin)' };

const dakika = (s) => {
    if (!s) return null;
    const [h, m] = s.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
};
const saatYaz = (dk) => {
    const t = Math.round(dk);
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};
const sureYaz = (dk) => (dk >= 60 ? `${Math.floor(dk / 60)} sa ${dk % 60} dk` : `${dk} dk`);
const sayiYaz = (x) => x.toLocaleString('tr-TR');

// Renk kuralı (kullanıcı 03.11.2026): giriş 09:15 sonrası kırmızı, 08:30 öncesi mavi; çıkış 18:30 öncesi kırmızı,
// tam 18:30 yeşil, sonrası mavi. Sorgu gün başına ilk ve son okutmayı döndürür (cihazın Giriş/Çıkış işareti güvenilir
// değil): ilk okutma giriş, son okutma çıkış; tek okutmalı günde ikisi aynı saattir.
const girisRengi = (dk) => (dk > 9 * 60 + 15 ? 'red' : dk < 8 * 60 + 30 ? 'blue' : 'green');
const cikisRengi = (dk) => (dk < 18 * 60 + 30 ? 'red' : dk > 18 * 60 + 30 ? 'blue' : 'green');
const okutmaSatiri = (r) => {
    const giris = r['İlk Saat'] || '';
    const cikis = r['Son Saat'] || '';
    return {
        date: r.Tarih || '',
        giris,
        cikis,
        girisColor: giris ? girisRengi(dakika(giris)) : 'orange',
        cikisColor: cikis ? cikisRengi(dakika(cikis)) : 'orange'
    };
};

const takvimGunleri = (ay) => {
    const ilk = ay.startOf('month');
    const bas = ilk.subtract((ilk.day() + 6) % 7, 'day');
    const son = ay.endOf('month');
    const bit = son.add((7 - son.day()) % 7, 'day');
    const gunler = [];
    for (let g = bas; !g.isAfter(bit, 'day'); g = g.add(1, 'day')) {
        gunler.push({
            date: g.format('YYYY-MM-DD'),
            day: g.date(),
            isCurrentMonth: g.month() === ay.month(),
            isToday: g.isSame(dayjs(), 'day'),
            isWeekend: g.day() === 0 || g.day() === 6
        });
    }
    return gunler;
};

const GirisCikisTakvimi = ({ kullaniciNo, izinler = [] }) => {
    const { getAuthHeaders } = useAuth();
    const { darkMode } = useTheme();
    const [okutmalar, setOkutmalar] = useState([]);
    const [tatiller, setTatiller] = useState([]);
    const [ay, setAy] = useState(dayjs());
    const [seciliGun, setSeciliGun] = useState(null);

    useEffect(() => {
        if (!kullaniciNo) return undefined;
        let iptal = false;
        const sorgu = (ad) => axios.post(
            `${API_BASE_URL}/list/execute-query/${ad}`,
            { params: { id: kullaniciNo } },
            { headers: getAuthHeaders() }
        ).then((r) => (Array.isArray(r.data) ? r.data : [])).catch((e) => {
            console.error(`${ad} yüklenemedi:`, e);
            return [];
        });
        Promise.all([sorgu('profil-giris-cikis'), sorgu('profil-resmi-tatil')]).then(([okutma, tatil]) => {
            if (iptal) return;
            setOkutmalar(okutma.map(okutmaSatiri));
            setTatiller(tatil);
        });
        return () => { iptal = true; };
    }, [kullaniciNo]); // eslint-disable-line react-hooks/exhaustive-deps

    const tatilGunleri = {};
    tatiller.forEach((t) => {
        const gun = (t.tarih || '').slice(0, 10);
        (tatilGunleri[gun] = tatilGunleri[gun] || []).push({ ad: t.ad, yarim: !!t.yarim_gun });
    });

    // İzin günleri; ilk gün 12:00 ve sonrası başlıyorsa öğleden sonra, son gün 13:30'a kadar bitiyorsa sabah yarım günü
    const izinGunleri = {};
    izinler.forEach((iz) => {
        const bas = dayjs(iz.startDate);
        const bit = iz.endDate ? dayjs(iz.endDate) : bas;
        if (!bas.isValid() || !bit.isValid()) return;
        for (let g = bas, n = 0; !g.isAfter(bit, 'day') && n < 400; g = g.add(1, 'day'), n++) {
            const ogledenSonra = g.isSame(bas, 'day') && dakika(iz.startTime) >= 12 * 60;
            const sabah = g.isSame(bit, 'day') && dakika(iz.endTime) !== null && dakika(iz.endTime) <= 13 * 60 + 30;
            const gun = g.format('YYYY-MM-DD');
            (izinGunleri[gun] = izinGunleri[gun] || []).push({
                tip: iz.type || 'İzin', saat: iz.hours, sebep: iz.reason, onay: iz.approval === 'Onaylandı',
                kisim: ogledenSonra ? 'ogle' : sabah ? 'sabah' : 'tam',
                tur: /rapor/i.test(iz.type || '') ? 'rapor' : 'izin'
            });
        }
    });

    const okutmaGunleri = {};
    okutmalar.forEach((a) => {
        okutmaGunleri[dayjs(a.date, 'DD.MM.YYYY').format('YYYY-MM-DD')] = a;
    });

    const gunBilgisi = (tarih) => ({
        tatil: tatilGunleri[tarih] || [],
        izin: izinGunleri[tarih] || [],
        okutma: okutmaGunleri[tarih] || null
    });

    // Zemin: sabah / öğleden sonra ayrı; yarım günler köşeden ikiye bölünür (arife tatili öğleden sonradır). Renkler
    // değişken olarak verilir (--gc-s / --gc-o), hover'daki koyu ton CSS'te (GirisCikisTakvimi.css). Onaylanmamış izin
    // ("Onaylanmadı": bekleyen ya da reddedilen — Fedora ikisini ayırmıyor) de rengini alır, hücre taranır (onaysizMi).
    const gunZemini = ({ tatil, izin }) => {
        let sabah = null;
        let ogle = null;
        tatil.forEach((t) => {
            if (!t.yarim) sabah = 'tatil';
            ogle = 'tatil';
        });
        [...izin.filter((i) => i.onay), ...izin.filter((i) => !i.onay)].forEach((i) => {
            if (i.kisim !== 'ogle') sabah = sabah || i.tur;
            if (i.kisim !== 'sabah') ogle = ogle || i.tur;
        });
        if (!sabah && !ogle) return undefined;
        return { '--gc-s': sabah ? ZEMIN[sabah] : undefined, '--gc-o': ogle ? ZEMIN[ogle] : undefined };
    };
    const onaysizMi = ({ izin }) => izin.length > 0 && izin.every((i) => !i.onay);

    const ayOzeti = () => {
        const anahtar = ay.format('YYYY-MM');
        const gunler = Object.keys(okutmaGunleri).filter((t) => t.startsWith(anahtar)).map((t) => okutmaGunleri[t]);
        const ikili = gunler.filter((g) => g.giris && g.cikis && g.giris !== g.cikis);
        const ort = (liste) => (liste.length ? saatYaz(liste.reduce((a, b) => a + b, 0) / liste.length) : '—');
        let izin = 0;
        let tatil = 0;
        for (let g = ay.startOf('month'); g.month() === ay.month(); g = g.add(1, 'day')) {
            if (g.day() === 0 || g.day() === 6) continue;
            const b = gunBilgisi(g.format('YYYY-MM-DD'));
            const tatilPayi = b.tatil.some((t) => !t.yarim) ? 1 : b.tatil.length ? 0.5 : 0;
            const onayli = b.izin.filter((i) => i.onay);
            tatil += tatilPayi;
            if (onayli.length && tatilPayi < 1) izin += onayli.some((i) => i.kisim === 'tam') ? 1 - tatilPayi : 0.5;
        }
        return {
            calisilan: gunler.length,
            ortGiris: ort(gunler.map((g) => dakika(g.giris)).filter((x) => x !== null)),
            ortCikis: ort(ikili.map((g) => dakika(g.cikis))),
            gec: gunler.filter((g) => dakika(g.giris) > 9 * 60 + 15).length,
            erken: ikili.filter((g) => dakika(g.cikis) < 18 * 60 + 30).length,
            izin,
            tatil
        };
    };

    const gunTikla = (tarih) => {
        const b = gunBilgisi(tarih);
        if (b.okutma || b.tatil.length || b.izin.length) setSeciliGun(tarih);
    };

    const oz = ayOzeti();
    const koyu = darkMode ? ' gc-koyu' : '';

    return (
        <Card className={`profil-card gc-kart${koyu}`}>
            <div className="gc-baslik">
                <Title level={5} style={{ margin: 0 }}>Giriş-Çıkış</Title>
                <Space size={4}>
                    <Button type="text" size="small" onClick={() => setAy(ay.subtract(1, 'month'))} icon={<LeftOutlined />} aria-label="Önceki ay" />
                    <Text strong className="gc-ay">{AY_ADLARI[ay.month()]} {ay.year()}</Text>
                    <Button type="text" size="small" onClick={() => setAy(ay.add(1, 'month'))} icon={<RightOutlined />} aria-label="Sonraki ay" />
                    <Button size="small" onClick={() => setAy(dayjs())} disabled={ay.isSame(dayjs(), 'month')}>Bugün</Button>
                </Space>
            </div>
            <div className="gc-ozet">
                <div className="gc-ozet-kutu"><span>Çalışılan Gün</span><b>{oz.calisilan}</b></div>
                <div className="gc-ozet-kutu"><span>Ort. Giriş</span><b>{oz.ortGiris}</b></div>
                <div className="gc-ozet-kutu"><span>Ort. Çıkış</span><b>{oz.ortCikis}</b></div>
                <div className="gc-ozet-kutu"><span>Geç Giriş</span><b className={oz.gec ? 'kirmizi' : ''}>{oz.gec}</b></div>
                <div className="gc-ozet-kutu"><span>Erken Çıkış</span><b className={oz.erken ? 'kirmizi' : ''}>{oz.erken}</b></div>
                <div className="gc-ozet-kutu"><span>İzin · Tatil</span><b>{sayiYaz(oz.izin)} · {sayiYaz(oz.tatil)} <small>gün</small></b></div>
            </div>
            <div className="gc-takvim">
                {HAFTA_GUNLERI.map((gun, i) => (
                    <div key={`b${i}`} className={`gc-hafta-gunu${i > 4 ? ' haftasonu' : ''}`}>{gun}</div>
                ))}
                {takvimGunleri(ay).map((day) => {
                    const bilgi = gunBilgisi(day.date);
                    const o = bilgi.okutma;
                    const tek = o && o.giris && o.giris === o.cikis;
                    const dolu = o || bilgi.tatil.length || bilgi.izin.length;
                    const siniflar = [
                        'gc-gun',
                        day.isCurrentMonth ? '' : 'pasif',
                        day.isWeekend ? 'haftasonu' : '',
                        day.isToday ? 'bugun' : '',
                        dolu ? 'dolu' : '',
                        onaysizMi(bilgi) ? 'onaysiz' : ''
                    ].filter(Boolean).join(' ');
                    return (
                        <div key={day.date} className={siniflar} style={gunZemini(bilgi)}
                             onClick={() => gunTikla(day.date)}>
                            <div className="gc-gun-ust">
                                <div className="gc-gun-no">{day.day}</div>
                                {day.isToday && <span className="gc-bugun-rozet">Bugün</span>}
                            </div>
                            {bilgi.tatil.map((t, i) => (
                                <div key={`t${i}`} className="gc-etiket tatil"
                                     title={`${t.ad}${t.yarim ? ' · yarım gün (13:00 sonrası)' : ''}`}>
                                    {t.yarim && <b>½ </b>}{t.ad}
                                </div>
                            ))}
                            {bilgi.izin.map((iz, i) => (
                                <div key={`i${i}`} className={`gc-etiket ${iz.tur}${iz.onay ? '' : ' bekliyor'}`}
                                     title={`${iz.tip} · ${KISIM_ADI[iz.kisim]}${iz.onay ? '' : ' · onaylanmadı'}`}>
                                    {iz.kisim !== 'tam' && <b>½ </b>}{iz.tip}
                                </div>
                            ))}
                            {o && !tek && o.giris && (
                                <div className={`gc-saat ${RENK_SINIF[o.girisColor] || ''}`}>
                                    <span className="gc-kelime">Giriş</span>{o.giris}
                                </div>
                            )}
                            {o && !tek && o.cikis && (
                                <div className={`gc-saat ${RENK_SINIF[o.cikisColor] || ''}`}>
                                    <span className="gc-kelime">Çıkış</span>{o.cikis}
                                </div>
                            )}
                            {tek && (
                                <div className="gc-saat turuncu" title="Tek okutma: çıkış kaydı yok">
                                    <span className="gc-kelime">Tek</span>{o.giris}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="gc-lejant">
                <span><i className="gc-nokta yesil" />Zamanında</span>
                <span><i className="gc-nokta kirmizi" />Geç giriş · erken çıkış</span>
                <span><i className="gc-nokta mavi" />Erken giriş · 18:30 sonrası</span>
                <span><i className="gc-nokta turuncu" />Tek okutma</span>
                <span><i className="gc-kare tatil" />Resmi tatil</span>
                <span><i className="gc-kare izin" />İzin</span>
                <span><i className="gc-kare rapor" />Rapor</span>
                <span><i className="gc-kare onaysiz" />Onaylanmamış izin</span>
                <span><i className="gc-kare yarim" />Yarım gün</span>
                <span><i className="gc-kare haftasonu" />Hafta sonu</span>
            </div>

            {/* Gün detayı: resmi tatil, izin, giriş-çıkış */}
            <Modal
                title={seciliGun ? (() => {
                    const g = dayjs(seciliGun);
                    return `${g.date()} ${AY_ADLARI[g.month()]} ${g.year()}, ${GUN_ADLARI[g.day()]}`;
                })() : ''}
                open={!!seciliGun}
                onCancel={() => setSeciliGun(null)}
                footer={null}
                width={460}
                className={`gc-modal${darkMode ? ' dark-modal' : ''}${koyu}`}
            >
                {seciliGun && (() => {
                    const b = gunBilgisi(seciliGun);
                    const o = b.okutma;
                    const tek = o && o.giris === o.cikis;
                    const satir = (renk, ad, saat, aciklama) => (
                        <div className="gc-detay-satir">
                            <i className={`gc-nokta ${RENK_SINIF[renk] || ''}`} />
                            <span className="gc-detay-etiket">{ad}</span>
                            <b className="gc-detay-saat">{saat}</b>
                            <span className={`gc-rozet ${RENK_SINIF[renk] || ''}`}>{aciklama}</span>
                        </div>
                    );
                    return (
                        <div className="gc-detay">
                            {b.tatil.map((t, i) => (
                                <div key={`t${i}`} className="gc-detay-kutu tatil">
                                    <div className="gc-detay-ust">Resmi Tatil</div>
                                    <div className="gc-detay-ad">{t.ad}</div>
                                    <div className="gc-detay-alt">{t.yarim ? 'Yarım gün · 13:00\'ten sonra tatil' : 'Tam gün'}</div>
                                </div>
                            ))}
                            {b.izin.map((iz, i) => (
                                <div key={`i${i}`} className={`gc-detay-kutu ${iz.tur}${iz.onay ? '' : ' bekliyor'}`}>
                                    <div className="gc-detay-ust">{iz.tur === 'rapor' ? 'Rapor' : 'İzin'}{iz.onay ? '' : ' · Onaylanmadı'}</div>
                                    <div className="gc-detay-ad">{iz.tip}</div>
                                    <div className="gc-detay-alt">
                                        {KISIM_ADI[iz.kisim]}{iz.saat ? ` · ${iz.saat}` : ''}{iz.sebep ? ` · ${iz.sebep}` : ''}
                                    </div>
                                </div>
                            ))}
                            {o ? (
                                <div className="gc-detay-kutu okutma">
                                    <div className="gc-detay-ust">Giriş-Çıkış</div>
                                    {tek ? satir('orange', 'Okutma', o.giris, 'Tek okutma · çıkış kaydı yok') : (
                                        <>
                                            {satir(o.girisColor, 'Giriş', o.giris, GIRIS_ACIKLAMA[o.girisColor])}
                                            {satir(o.cikisColor, 'Çıkış', o.cikis, CIKIS_ACIKLAMA[o.cikisColor])}
                                            <div className="gc-detay-alt">
                                                Ofiste geçen süre: <b>{sureYaz(dakika(o.cikis) - dakika(o.giris))}</b>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="gc-detay-bos">Bu gün için giriş-çıkış kaydı yok.</div>
                            )}
                        </div>
                    );
                })()}
            </Modal>
        </Card>
    );
};

export default GirisCikisTakvimi;
